'use client';

import React, { useEffect, useState } from 'react';
import { fetchOverlayNearest } from '../utils/overlaysApi';
import { FaGasPump, FaMapMarkerAlt, FaExternalLinkAlt, FaClock, FaChevronDown, FaChevronUp } from 'react-icons/fa';

interface FuelStation {
  place_id: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  distance_km: number;
  brand: string;
  open_now?: boolean | null;
  rating?: number;
}

interface FuelCheckerInfo {
  name: string;
  url: string;
}

interface NearbyFuelStationsProps {
  latitude?: string;
  longitude?: string;
  state?: string;
  radiusKm?: number;
  campsiteName?: string;
}

const STATE_FUEL_CHECKERS: Record<string, FuelCheckerInfo> = {
  'NSW': { name: 'FuelCheck NSW', url: 'https://www.fuelcheck.nsw.gov.au/app' },
  'ACT': { name: 'FuelCheck NSW', url: 'https://www.fuelcheck.nsw.gov.au/app' },
  'QLD': { name: 'RACQ Fair Fuel', url: 'https://www.racq.com.au/car/fuel-prices' },
  'WA':  { name: 'FuelWatch WA', url: 'https://www.fuelwatch.wa.gov.au/' },
  'SA':  { name: 'SA Fuel Pricing', url: 'https://www.sa.gov.au/topics/driving-and-transport/fuel-pricing' },
  'VIC': { name: 'ServoSaver', url: 'https://servosaver.com.au/' },
  'TAS': { name: 'FuelCheck TAS', url: 'https://www.fuelcheck.tas.gov.au/' },
  'NT':  { name: 'MyFuel NT', url: 'https://nt.gov.au/driving/fuel/my-fuel-nt' },
};

const BRAND_COLORS: Record<string, string> = {
  'Shell': '#FBCE07',
  'BP': '#009B3A',
  'Caltex': '#ED1C24',
  'Ampol': '#0033A0',
  '7-Eleven': '#4CAF50',
  'United': '#1a68d1',
  'Liberty': '#FF6600',
  'Puma': '#E4002B',
  'Costco': '#E31837',
};

const BRAND_NAMES = ['Shell', 'BP', 'Caltex', 'Ampol', '7-Eleven', 'United',
  'Liberty', 'Puma', 'Mobil', 'Coles Express', 'Costco', 'Metro',
  'Woolworths', 'Speedway', 'Independent'];

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function detectBrand(name: string): string {
  const lower = name.toLowerCase();
  for (const b of BRAND_NAMES) {
    if (lower.includes(b.toLowerCase())) return b;
  }
  return '';
}

export default function NearbyFuelStations({
  latitude,
  longitude,
  state,
  radiusKm = 30,
  campsiteName,
}: NearbyFuelStationsProps) {
  const [stations, setStations] = useState<FuelStation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fuelChecker = state ? STATE_FUEL_CHECKERS[state.toUpperCase()] || null : null;

  useEffect(() => {
    if (!latitude || !longitude) {
      setLoading(false);
      return;
    }

    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (isNaN(lat) || isNaN(lng)) {
      setLoading(false);
      return;
    }

    const ctrl = new AbortController();
    // Fuel stations come from our own OSM-sourced overlay endpoint (server-side
    // nearest search) — no Google Places call, so this costs nothing per view.
    (async () => {
      try {
        const res = await fetchOverlayNearest('fuel', lat, lng, {
          limit: 10,
          radiusKm,
          signal: ctrl.signal,
        });
        const fuelStations: FuelStation[] = res.points.map((p) => ({
          place_id: p.id,
          name: p.name || 'Fuel Station',
          address: p.addr || '',
          latitude: p.lat,
          longitude: p.lng,
          distance_km: Math.round((p.distance_km ?? haversineKm(lat, lng, p.lat, p.lng)) * 10) / 10,
          brand: p.brand || detectBrand(p.name || ''),
          open_now: null, // OSM has opening hours text, not a live open/closed state
        }));
        setStations(fuelStations);
        setLoading(false);
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') return;
        console.error('[NearbyFuelStations] Error:', err);
        setError(true);
        setLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, [latitude, longitude, radiusKm]);

  if (!latitude || !longitude) return null;

  if (loading) {
    return (
      <div className="ca-fuel bg-white rounded-lg shadow-sm border p-6">        <div className="flex items-center gap-2 mb-4">
          <FaGasPump className="text-orange-500" />
          <h3 className="text-lg font-semibold">Nearby Fuel</h3>
        </div>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error || stations.length === 0) {
    return (
      <div className="ca-fuel bg-white rounded-lg shadow-sm border p-6">        <div className="flex items-center gap-2 mb-3">
          <FaGasPump className="text-orange-500" />
          <h3 className="text-lg font-semibold">Fuel Information</h3>
        </div>
        {stations.length === 0 && !error && (
          <p className="text-sm text-gray-600 mb-3">
            No fuel stations found within {radiusKm}km. This may be a remote area — plan your fuel accordingly.
          </p>
        )}
        {fuelChecker && (
          <a
            href={fuelChecker.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-[#1558b0] hover:text-[#123f7d] font-medium"
          >
            Check prices on {fuelChecker.name}
            <FaExternalLinkAlt className="ca-ext text-[10px]" />
          </a>
        )}
        {!fuelChecker && (
          <a
            href={`https://www.google.com/maps/search/fuel+station/@${latitude},${longitude},12z`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-[#1558b0] hover:text-[#123f7d] font-medium"
          >
            Find fuel on Google Maps
            <FaExternalLinkAlt className="ca-ext text-[10px]" />
          </a>
        )}
      </div>
    );
  }

  const nearest = stations[0];
  const displayStations = expanded ? stations : stations.slice(0, 3);
  const isRemote = nearest.distance_km > 50;

  return (
    <div className="ca-fuel bg-white rounded-lg shadow-sm border p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FaGasPump className="text-orange-500" />
          <h3 className="text-lg font-semibold">Nearby Fuel</h3>
        </div>
        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
          within {radiusKm}km
        </span>
      </div>

      {/* Remote area warning */}
      {isRemote && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4 text-sm">
          <span className="font-medium text-amber-800">&#x26A0;&#xFE0F; Remote Area</span>
          <p className="text-amber-700 mt-0.5">
            Nearest fuel is {nearest.distance_km}km away. Fill up before heading out.
          </p>
        </div>
      )}

      {/* Station list */}
      <div className="space-y-3">
        {displayStations.map((station, idx) => (
          <a
            key={station.place_id}
            href={`https://www.google.com/maps/dir/?api=1&destination=${station.latitude},${station.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            className="ca-fuel__row flex items-start gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors group -mx-1"
          >
            {/* Brand dot */}
            <div
              className="ca-fuel__dot w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{
                backgroundColor: BRAND_COLORS[station.brand] || '#6B7280',
                opacity: 0.9,
              }}
            >
              <FaGasPump className="text-white text-xs" />
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900 truncate">
                  {station.name}
                </span>
                {idx === 0 && (
                  <span className="ca-fuel__nearest text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                    Nearest
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 truncate">{station.address}</p>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-xs font-medium text-gray-700">
                  <FaMapMarkerAlt className="inline text-[10px] mr-0.5 text-gray-400" />
                  {station.distance_km}km
                </span>
                {station.open_now !== null && station.open_now !== undefined && (
                  <span className={`text-xs ${station.open_now ? 'text-green-600' : 'text-red-500'}`}>
                    <FaClock className="inline text-[10px] mr-0.5" />
                    {station.open_now ? 'Open' : 'Closed'}
                  </span>
                )}
              </div>
            </div>

            {/* Direction arrow */}
            <FaExternalLinkAlt className="ca-ext text-gray-300 group-hover:text-accent text-[10px] mt-2 flex-shrink-0 transition-colors" />
          </a>
        ))}
      </div>

      {/* Show more/less */}
      {stations.length > 3 && (
        <button
          onClick={(e) => {
            e.preventDefault();
            setExpanded(!expanded);
          }}
          className="flex items-center justify-center gap-1 w-full mt-3 py-2 text-xs text-gray-500 hover:text-gray-700 transition-colors"
        >
          {expanded ? (
            <>Show fewer <FaChevronUp className="text-[10px]" /></>
          ) : (
            <>Show {stations.length - 3} more <FaChevronDown className="text-[10px]" /></>
          )}
        </button>
      )}

      {/* Divider + State fuel checker link */}
      {fuelChecker && (
        <div className="border-t mt-3 pt-3">
          <a
            href={fuelChecker.url}
            target="_blank"
            rel="noopener noreferrer"
            className="ca-fuel__more flex items-center justify-between text-sm text-[#1558b0] hover:text-[#123f7d] transition-colors"
          >
            <span className="font-medium">Compare prices on {fuelChecker.name}</span>
            <FaExternalLinkAlt className="ca-ext text-[10px]" />
          </a>
        </div>
      )}

      {/* Google Maps search link */}
      <div className={`${fuelChecker ? 'mt-2' : 'border-t mt-3 pt-3'}`}>
        <a
          href={`https://www.google.com/maps/search/fuel+station/@${latitude},${longitude},12z`}
          target="_blank"
          rel="noopener noreferrer"
          className="ca-fuel__more flex items-center justify-between text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <span>View all on Google Maps</span>
          <FaExternalLinkAlt className="ca-ext text-[10px]" />
        </a>
      </div>
    </div>
  );
}
